/**
 * Parameter runtime evaluation.
 *
 * Given a DynamicParameter (post `applyInstanceParameters` overlay —
 * so `currentDistance` / `currentValue` / `currentStateId` / `currentPoint`
 * are already the instance-specific values), compute a typed runtime
 * record with derived geometry (direction angle for Linear, current state
 * for Visibility, …) that downstream actions and UI both consume.
 *
 * Keeping this layer separate from action application lets the grip
 * renderer and property palette call the same function without pulling
 * in action-dispatch code.
 *
 * DYN-2 additions (Tier 2): Point / Polar / XY / Rotation / Lookup
 * parameter kinds. Value-set snapping is shared across all numeric kinds
 * (Linear.valueSet, Polar.distanceValueSet/angleValueSet, XY.xValueSet/
 * yValueSet, Rotation.angleValueSet).
 */

import type {
  AlignedConstraintParameter,
  AlignmentParameter,
  AngularConstraintParameter,
  DiameterConstraintParameter,
  DynamicParameter,
  HorizontalConstraintParameter,
  LinearParameter,
  PointParameter,
  PolarParameter,
  RadialConstraintParameter,
  RotationParameter,
  ValueSet,
  VerticalConstraintParameter,
  XYParameter,
} from "@dxf-ai-editor/drawing-model";

export interface Vec2Lite {
  readonly x: number;
  readonly y: number;
}

export interface ParameterRuntimeValue {
  readonly parameterId: string;
  readonly parameterName: string;
  readonly kind:
    | "linear"
    | "flip"
    | "visibility"
    | "basepoint"
    | "point"
    | "polar"
    | "xy"
    | "rotation"
    | "lookup"
    | "constraint-horizontal"
    | "constraint-vertical"
    | "constraint-angular"
    | "alignment"
    | "constraint-aligned"
    | "constraint-radial"
    | "constraint-diameter"
    // DYN-4 Fix 3 — ACDBASSOCVARIABLE-backed scalar driver.
    | "user";
  /** Linear / Polar / Horizontal / Vertical constraint: current distance after Value-Set snap. */
  readonly distance?: number;
  /**
   * Linear: direction angle in radians (basePoint → secondPoint).
   * Polar / Rotation / Angular constraint: current angle in RADIANS
   * (schema stores degrees; we convert once here so actions can consume
   * radians uniformly).
   */
  readonly angle?: number;
  /** Flip: resolved boolean state. */
  readonly flipped?: boolean;
  /** Visibility: active state id (always present when kind === "visibility"). */
  readonly stateId?: string;
  /** BasePoint: position of the override grip relative to block origin. */
  readonly basePointOffset?: Vec2Lite;
  /** Point: snapped current point (in block coords). */
  readonly point?: Vec2Lite;
  /** Polar: { distance (snapped), angle (radians, snapped) }. */
  readonly polar?: { distance: number; angle: number };
  /** XY: { x, y } — snapped current distances from basePoint. */
  readonly xy?: { x: number; y: number };
  /** Rotation: current angle in RADIANS (snapped). */
  readonly rotationAngle?: number;
  /** Lookup: current row id (string) if set. */
  readonly lookupRowId?: string;
  /**
   * Horizontal / Vertical constraint: snapped distance value (also
   * mirrored on `distance` for action-dispatch convenience).
   */
  readonly constraintDistance?: number;
  /**
   * Angular constraint: snapped angle in RADIANS (also mirrored on
   * `angle` for action-dispatch convenience).
   */
  readonly constraintAngle?: number;
}

/* ── helpers ───────────────────────────────────────────────────── */

function axisAngleRad(p: LinearParameter): number {
  return Math.atan2(
    p.secondPoint.y - p.basePoint.y,
    p.secondPoint.x - p.basePoint.x,
  );
}

/**
 * AutoCAD Value-Set snap. Three modes:
 *   - "none"       — raw value
 *   - "increment"  — snap to step grid, bounded by [min, max]
 *   - "list"       — snap to nearest entry in listValues
 *
 * Defensive: a malformed set (increment mode with no increment, or list
 * mode with empty list) silently falls back to the raw value.
 */
export function snapValueSet(raw: number, set: ValueSet | undefined): number {
  if (!set) return raw;
  switch (set.mode) {
    case "none":
      return raw;
    case "list": {
      if (!set.listValues || set.listValues.length === 0) return raw;
      let bestIdx = 0;
      let bestDelta = Math.abs(set.listValues[0]! - raw);
      for (let i = 1; i < set.listValues.length; i++) {
        const d = Math.abs(set.listValues[i]! - raw);
        if (d < bestDelta) {
          bestDelta = d;
          bestIdx = i;
        }
      }
      return set.listValues[bestIdx]!;
    }
    case "increment": {
      if (!set.increment || set.increment <= 0) return raw;
      let v = raw;
      if (set.min != null && v < set.min) v = set.min;
      if (set.max != null && v > set.max) v = set.max;
      const anchor = set.min ?? 0;
      const steps = Math.round((v - anchor) / set.increment);
      let snapped = anchor + steps * set.increment;
      if (set.min != null && snapped < set.min) snapped = set.min;
      if (set.max != null && snapped > set.max) snapped = set.max;
      return snapped;
    }
    default:
      return raw;
  }
}

/**
 * Linear-specific wrapper — keeps Tier 1 call sites happy. Identical
 * semantics to snapValueSet(raw, param.valueSet).
 */
function snapLinear(raw: number, param: LinearParameter): number {
  return snapValueSet(raw, param.valueSet);
}

const DEG_TO_RAD = Math.PI / 180;

/* ── main dispatch ─────────────────────────────────────────────── */

export function resolveParameterRuntimeValue(
  parameter: DynamicParameter,
): ParameterRuntimeValue {
  switch (parameter.kind) {
    case "linear":
      return {
        parameterId: parameter.id,
        parameterName: parameter.name,
        kind: "linear",
        distance: snapLinear(parameter.currentDistance, parameter),
        angle: axisAngleRad(parameter),
      };
    case "flip":
      return {
        parameterId: parameter.id,
        parameterName: parameter.name,
        kind: "flip",
        flipped: parameter.currentValue,
      };
    case "visibility":
      return {
        parameterId: parameter.id,
        parameterName: parameter.name,
        kind: "visibility",
        stateId: parameter.currentStateId,
      };
    case "basepoint":
      return {
        parameterId: parameter.id,
        parameterName: parameter.name,
        kind: "basepoint",
        basePointOffset: parameter.position,
      };
    case "point": {
      const p = parameter as PointParameter;
      const rawX = p.currentPoint.x - p.basePoint.x;
      const rawY = p.currentPoint.y - p.basePoint.y;
      const snappedX = snapValueSet(rawX, p.xValueSet);
      const snappedY = snapValueSet(rawY, p.yValueSet);
      return {
        parameterId: p.id,
        parameterName: p.name,
        kind: "point",
        point: {
          x: p.basePoint.x + snappedX,
          y: p.basePoint.y + snappedY,
        },
      };
    }
    case "polar": {
      const p = parameter as PolarParameter;
      const distance = snapValueSet(p.currentDistance, p.distanceValueSet);
      const angleDegSnapped = snapValueSet(p.currentAngle, p.angleValueSet);
      const angleRad = angleDegSnapped * DEG_TO_RAD;
      return {
        parameterId: p.id,
        parameterName: p.name,
        kind: "polar",
        distance,
        angle: angleRad,
        polar: { distance, angle: angleRad },
      };
    }
    case "xy": {
      const p = parameter as XYParameter;
      return {
        parameterId: p.id,
        parameterName: p.name,
        kind: "xy",
        xy: {
          x: snapValueSet(p.currentX, p.xValueSet),
          y: snapValueSet(p.currentY, p.yValueSet),
        },
      };
    }
    case "rotation": {
      const p = parameter as RotationParameter;
      const angleDegSnapped = snapValueSet(p.currentAngle, p.angleValueSet);
      const angleRad = angleDegSnapped * DEG_TO_RAD;
      return {
        parameterId: p.id,
        parameterName: p.name,
        kind: "rotation",
        angle: angleRad,
        rotationAngle: angleRad,
      };
    }
    case "lookup":
      return {
        parameterId: parameter.id,
        parameterName: parameter.name,
        kind: "lookup",
        lookupRowId: parameter.currentLookupRowId,
      };
    case "constraint-horizontal": {
      // Horizontal constraint locks pointB.x − pointA.x. The author
      // direction is +X; the linked Move/Stretch actions translate by
      // delta along that axis.
      const p = parameter as HorizontalConstraintParameter;
      const distance = snapValueSet(p.currentDistance, p.distanceValueSet);
      return {
        parameterId: p.id,
        parameterName: p.name,
        kind: "constraint-horizontal",
        distance,
        // Author-axis angle is 0 (horizontal). Some action paths read
        // `angle` to compute the translation direction — pin to 0
        // explicitly so Move/Stretch get a deterministic +X axis.
        angle: 0,
        constraintDistance: distance,
      };
    }
    case "constraint-vertical": {
      // Vertical constraint locks pointB.y − pointA.y. Author-axis
      // angle is π/2 (vertical, +Y).
      const p = parameter as VerticalConstraintParameter;
      const distance = snapValueSet(p.currentDistance, p.distanceValueSet);
      return {
        parameterId: p.id,
        parameterName: p.name,
        kind: "constraint-vertical",
        distance,
        angle: Math.PI / 2,
        constraintDistance: distance,
      };
    }
    case "constraint-angular": {
      // Angular constraint locks the angle ∠(vertex→pointA, vertex→pointB).
      // Stored in degrees; converted to radians for action consumption.
      const p = parameter as AngularConstraintParameter;
      const angleDegSnapped = snapValueSet(p.currentAngle, p.angleValueSet);
      const angleRad = angleDegSnapped * DEG_TO_RAD;
      return {
        parameterId: p.id,
        parameterName: p.name,
        kind: "constraint-angular",
        angle: angleRad,
        rotationAngle: angleRad,
        constraintAngle: angleRad,
      };
    }
    case "alignment": {
      // DYN-3 Tier 3: Alignment parameter is NOT driven by actions —
      // it writes its own rotation into `currentAngle` (degrees) when
      // INSERT auto-rotates to nearby geometry. The evaluator exposes
      // the resolved radian angle so downstream consumers (renderer,
      // property palette) apply it uniformly.
      const p = parameter as AlignmentParameter;
      const angleRad = p.currentAngle * DEG_TO_RAD;
      return {
        parameterId: p.id,
        parameterName: p.name,
        kind: "alignment",
        angle: angleRad,
        rotationAngle: angleRad,
        basePointOffset: p.position,
      };
    }
    case "constraint-aligned": {
      // DYN-3 Tier 3: Aligned distance constraint — locks the distance
      // between two arbitrary points. Evaluator surfaces `distance` +
      // `angle` (radians, authored pointA → pointB direction) so Move /
      // Stretch / Scale / Array can project their delta along the
      // aligned axis.
      const p = parameter as AlignedConstraintParameter;
      const distance = snapValueSet(p.currentDistance, p.distanceValueSet);
      const dx = p.pointB.x - p.pointA.x;
      const dy = p.pointB.y - p.pointA.y;
      const angleRad = Math.atan2(dy, dx);
      return {
        parameterId: p.id,
        parameterName: p.name,
        kind: "constraint-aligned",
        distance,
        angle: angleRad,
        constraintDistance: distance,
      };
    }
    case "constraint-radial": {
      // DYN-3 Tier 3: Radial constraint — locks circle / arc radius.
      // Evaluator mirrors `distance` = radius and `angle` = direction
      // center → radiusPoint (authored radial direction). Drives Scale
      // (uniform from center) via parameterScaleFactor in actions.ts.
      const p = parameter as RadialConstraintParameter;
      const distance = snapValueSet(p.currentRadius, p.radiusValueSet);
      const dx = p.radiusPoint.x - p.center.x;
      const dy = p.radiusPoint.y - p.center.y;
      const angleRad = Math.atan2(dy, dx);
      return {
        parameterId: p.id,
        parameterName: p.name,
        kind: "constraint-radial",
        distance,
        angle: angleRad,
        constraintDistance: distance,
      };
    }
    case "constraint-diameter": {
      // DYN-3 Tier 3: Diameter constraint — locks circle diameter (2×
      // radius). Evaluator surfaces `distance` = diameter and `angle`
      // = direction point1 → point2 (authored diameter line). Drives
      // Scale uniform about `center`.
      const p = parameter as DiameterConstraintParameter;
      const distance = snapValueSet(p.currentDiameter, p.diameterValueSet);
      const dx = p.point2.x - p.point1.x;
      const dy = p.point2.y - p.point1.y;
      const angleRad = Math.atan2(dy, dx);
      return {
        parameterId: p.id,
        parameterName: p.name,
        kind: "constraint-diameter",
        distance,
        angle: angleRad,
        constraintDistance: distance,
      };
    }
    case "user": {
      // DYN-4 Fix 3: User parameter — named scalar backed by
      // ACDBASSOCVARIABLE. Numeric scalar kinds (real / distance / area /
      // angle) surface as `distance` for compatibility with
      // Move / Stretch / Scale action dispatch (which read `runtime.distance`
      // for linear-like drivers). `string`-typed variables surface no
      // numeric value; downstream consumers read the authored
      // `defaultValue` directly from the parameter.
      const raw = parameter.defaultValue;
      const distance = typeof raw === "number" ? raw : undefined;
      const result: ParameterRuntimeValue = {
        parameterId: parameter.id,
        parameterName: parameter.name,
        kind: "user",
      };
      if (distance !== undefined) {
        return { ...result, distance };
      }
      return result;
    }
  }
}
